/**
 * Document URL slice – get-document-url API via createAsyncThunk.
 * Open IFS flow is driven fully by this slice state:
 * pending/fulfilled/rejected + dialog payload (no legacy message-box dispatches).
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ERROR_TYPE,
  WARNING_TYPE,
  INFO_TYPE,
  SERVER_WARNING_TYPE,
  FAILED_GENERATE_DIRECT_LINK,
} from "../../constants/messageTypes";
import AuthTeams from "../../../auth/AuthTeams";
import UserAuth from "../../../auth/userAuth";
import { appInsights } from "../../../azure/appInsights";
import {
  getDocumentUrl as getDocumentUrlApi,
  getIfsDocumentUrl as getIfsDocumentUrlApi,
  mapDocumentUrlError,
} from "../../api/documentsAPI";
import { serializeError } from "../../utils/serializeError";

const userAuth = new UserAuth();
const OPEN_IFS_SUCCESS_MESSAGE = "Opened IFS Cloud successfully.";

/** Payload: { items, docType, linkPlatform, aurenaShowDetails } */
export const getDocumentUrl = createAsyncThunk(
  "documents/documentUrl/getDocumentUrl",
  async (
    { items, docType, linkPlatform, aurenaShowDetails },
    { dispatch, rejectWithValue }
  ) => {
    const appInsightProps = {
      userId: new AuthTeams().getEmail(),
      functionName: "getDocumentUrl",
      statusCode: 0,
      items,
      docType,
      linkPlatform,
      aurenaShowDetails,
    };

    appInsights.trackTrace({
      message: "Sharing Document Url Requested",
      appInsightProps,
    });

    try {
      const res = await getDocumentUrlApi({
        items,
        docType,
        linkPlatform,
        aurenaShowDetails,
      });

      if (res.status === 200) {
        setTimeout(() => {
          dispatch(closeDocumentUrlDialog());
        }, 3000);
        return res.data;
      }
      return rejectWithValue(mapDocumentUrlError(res));
    } catch (error) {
      appInsightProps.statusCode =
        error.response === undefined ? 0 : error.response?.status;
      appInsights.trackException({
        error: error.toJSON?.() ?? error,
        appInsightProps,
      });
      setTimeout(() => {
        dispatch(closeDocumentUrlDialog());
      }, 3000);
      return rejectWithValue(mapDocumentUrlError(error));
    }
  }
);

/** Payload: { item (OneDrive/working doc), docType, linkPlatform, aurenaShowDetails } – uses get-ifs-document-url API. */
export const getDocumentUrlFromOneDrive = createAsyncThunk(
  "documents/documentUrl/getDocumentUrlFromOneDrive",
  async (
    { item, docType, linkPlatform, aurenaShowDetails },
    { dispatch, rejectWithValue }
  ) => {
    const appInsightProps = {
      userId: new AuthTeams().getEmail(),
      functionName: "getDocumentUrlFromOneDrive",
      statusCode: 0,
      item,
      docType,
      linkPlatform,
      aurenaShowDetails,
    };
    appInsights.trackTrace({
      message: "Sharing Document Url Requested (OneDrive)",
      appInsightProps,
    });
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const tokenTeams = tokenResponse ? tokenResponse.accessToken : null;
      const res = await getIfsDocumentUrlApi(
        { item, docType, linkPlatform, aurenaShowDetails },
        tokenTeams
      );
      if (res.status === 200 && res.data != null && res.data.length > 0) {
        const url = res.data[0];
        if (url !== null && url !== "" && url !== "Error" && url !== "Invalid") {
          setTimeout(() => dispatch(closeDocumentUrlDialog()), 3000);
          return url;
        }
      }
      return rejectWithValue(
        mapDocumentUrlError({ status: res.status, data: res.data })
      );
    } catch (error) {
      appInsightProps.statusCode =
        error.response === undefined ? 0 : error.response?.status;
      appInsights.trackException({
        error: error.toJSON?.() ?? error,
        appInsightProps,
      });
      setTimeout(() => dispatch(closeDocumentUrlDialog()), 3000);
      return rejectWithValue(mapDocumentUrlError(error));
    }
  }
);

const initialState = {
  loading: false,
  success: false,
  statusType: null,
  message: "",
  error: null,
  result: null,
  isDialogOpen: false,
  dialogType: "error",
  dialogTitle: "",
  dialogMessage: "",
  dialogDetails: "",
};

const documentUrlSlice = createSlice({
  name: "documents/documentUrl",
  initialState,
  reducers: {
    clearDocumentUrlError: (state) => {
      state.error = null;
    },
    closeDocumentUrlDialog: (state) => {
      state.isDialogOpen = false;
    
      if (state.statusType === "success" && state.dialogType === "info") {
        state.statusType = null;
        state.success = false;
        state.message = "";
        state.result = null;
        state.dialogTitle = "";
        state.dialogMessage = "";
        state.dialogDetails = "";
        state.dialogType = "error";
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDocumentUrl.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.statusType = "pending";
        state.message = "Generating URLs...";
        state.error = null;
        state.result = null;
        state.isDialogOpen = true;
        state.dialogType = "info";
        state.dialogTitle = "Please wait";
        state.dialogMessage = "Generating URLs...";
        state.dialogDetails = "";
      })
      .addCase(getDocumentUrl.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.statusType = "success";
        state.message = OPEN_IFS_SUCCESS_MESSAGE;
        state.error = null;
        state.result = action.payload;
        state.isDialogOpen = true;
        state.dialogType = "info";
        state.dialogTitle = "SUCCESS";
        state.dialogMessage = OPEN_IFS_SUCCESS_MESSAGE;
        state.dialogDetails = "";
      })
      .addCase(getDocumentUrl.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.statusType = "error";
        state.error = action.payload ?? serializeError(action.error) ?? { message: "Unknown error" };
        state.result = null;
        const payload = state.error ?? {};
        const type = payload.type ?? ERROR_TYPE;
        state.isDialogOpen = true;
        state.dialogTitle = type;
        state.dialogMessage = payload.message ?? FAILED_GENERATE_DIRECT_LINK;
        state.message = state.dialogMessage;
        state.dialogDetails = payload.errorMessage ?? "";
        if (type === WARNING_TYPE || type === SERVER_WARNING_TYPE) {
          state.dialogType = "warning";
        } else if (type === INFO_TYPE) {
          state.dialogType = "info";
        } else {
          state.dialogType = "error";
        }
      })
      .addCase(getDocumentUrlFromOneDrive.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.statusType = "pending";
        state.message = "Generating URLs...";
        state.error = null;
        state.result = null;
        state.isDialogOpen = true;
        state.dialogType = "info";
        state.dialogTitle = "Please wait";
        state.dialogMessage = "Generating URLs...";
        state.dialogDetails = "";
      })
      .addCase(getDocumentUrlFromOneDrive.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.statusType = "success";
        state.message = OPEN_IFS_SUCCESS_MESSAGE;
        state.error = null;
        state.result = action.payload;
        state.isDialogOpen = true;
        state.dialogType = "info";
        state.dialogTitle = "SUCCESS";
        state.dialogMessage = OPEN_IFS_SUCCESS_MESSAGE;
        state.dialogDetails = "";
      })
      .addCase(getDocumentUrlFromOneDrive.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.statusType = "error";
        state.error = action.payload ?? serializeError(action.error) ?? { message: "Unknown error" };
        state.result = null;
        const payload = state.error ?? {};
        const type = payload.type ?? ERROR_TYPE;
        state.isDialogOpen = true;
        state.dialogTitle = type;
        state.dialogMessage = payload.message ?? FAILED_GENERATE_DIRECT_LINK;
        state.message = state.dialogMessage;
        state.dialogDetails = payload.errorMessage ?? "";
        if (type === WARNING_TYPE || type === SERVER_WARNING_TYPE) {
          state.dialogType = "warning";
        } else if (type === INFO_TYPE) {
          state.dialogType = "info";
        } else {
          state.dialogType = "error";
        }
      });
  },
});

export const {
  clearDocumentUrlError,
  closeDocumentUrlDialog,
} = documentUrlSlice.actions;
export default documentUrlSlice.reducer;
